import React, {PureComponent} from 'react'
import PropTypes from 'prop-types'
import objectFitImages from 'object-fit-images'
import {equals} from 'ramda'
import ms from 'ms'

import {ScreenSizeContext} from '../../common-components/screen-size-helper'
import {PictureContext, TransformPropTypes} from './context'
import {StyledPicture, StyledImg, PictureContainer} from './styles'

import {imageTransform} from './buttercms-image-transform'

const prepareUrls = (url, options = {}) => {
  const orgSrc = imageTransform(url, {...options})

  if (!orgSrc) {
    return {
      mobileSrc: url,
      desktopSrc: url,
      orgSrc: url,
    }
  }

  return {
    mobileSrc: imageTransform(url, {...options, mobile: true}),
    mobileWebpSrc: imageTransform(url, {...options, mobile: true, format: 'webp'}),
    desktopSrc: imageTransform(url, {...options}),
    desktopWebpSrc: imageTransform(url, {...options, format: 'webp'}),
    // only use preview images when we actually have a width specified
    previewSrc:
      options.disableResize || !options.width
        ? undefined
        : imageTransform(url, {...options, width: 300, optimize: true, disableResize: false}),
    orgSrc,
  }
}

class Picture extends PureComponent {
  static propTypes = {
    alt: PropTypes.string,
    objectFit: PropTypes.string,
    objectPosition: PropTypes.string,
    blur: PropTypes.number,
    // does throw errors for post images but can not define a default value without breaking pictures in posts
    // eslint-disable-next-line react/require-default-props
    src: PropTypes.string,
    progressive: PropTypes.bool,
    enforceRatio: PropTypes.number,
    defaultOptions: TransformPropTypes,
    optionOverwrites: TransformPropTypes,
    screenSizeUpdate: PropTypes.number,
    disableResize: PropTypes.bool,
  }

  static defaultProps = {
    objectFit: null,
    objectPosition: 'center',
    blur: null,
    progressive: false,
    alt: '',
    enforceRatio: null,
    defaultOptions: {},
    optionOverwrites: {},
    screenSizeUpdate: 0,
    disableResize: false,
  }

  ensureBestImageQualityTimeout

  constructor(props) {
    super(props)
    this.imgRef = React.createRef()
    this.previewRef = React.createRef()
    this.containerRef = React.createRef()

    const {src, blur, progressive, defaultOptions, optionOverwrites, enforceRatio, disableResize} =
      this.props

    const {desktopSrc, desktopWebpSrc, mobileSrc, mobileWebpSrc, orgSrc, previewSrc} = prepareUrls(
      src || '',
      {
        ...defaultOptions,
        blur,
        progressive,
        disableResize,
        height: enforceRatio ? w => Math.round(w / enforceRatio) : undefined,
        ...optionOverwrites,
      }
    )

    this.state = {
      loaded: false,
      width: null,
      containerWidth: null,
      desktopSrc,
      desktopWebpSrc,
      mobileSrc,
      mobileWebpSrc,
      orgSrc,
      previewSrc,
    }
  }

  componentDidMount() {
    const {objectFit} = this.props
    if (objectFit) {
      objectFitImages(this.imgRef.current)
    }

    const width = this.containerRef.current?.clientWidth
    if (width) {
      this.setState({width, containerWidth: width})
    }
  }

  componentDidUpdate(prevProps, prevState) {
    const {src, blur, progressive, defaultOptions, optionOverwrites, screenSizeUpdate} = this.props
    const {width} = this.state

    if (prevProps.screenSizeUpdate !== screenSizeUpdate) {
      const containerWidth = this.containerRef.current?.clientWidth
      if (containerWidth) {
        // eslint-disable-next-line react/no-did-update-set-state
        this.setState({containerWidth})
      }

      clearTimeout(this.ensureBestImageQualityTimeout)
      this.ensureBestImageQualityTimeout = setTimeout(
        () => this.handleEnsureBestImageQuality(),
        ms('750ms')
      )
    }

    if (
      prevProps.src !== src ||
      prevProps.blur !== blur ||
      prevProps.progressive !== progressive ||
      !equals(prevProps.defaultOptions, defaultOptions) ||
      !equals(prevProps.defaultOptions, optionOverwrites) ||
      prevState.width !== width
    ) {
      this.buildImageTransformedSources()
    }
  }

  componentWillUnmount() {
    clearTimeout(this.ensureBestImageQualityTimeout)
  }

  handleEnsureBestImageQuality() {
    const {containerWidth, width} = this.state

    // make sure that we are increasing the image width as well if container width got bigger
    if (containerWidth > width) {
      this.setState({width: containerWidth})
    }
  }

  buildImageTransformedSources() {
    const {
      src,
      blur,
      progressive,
      defaultOptions,
      optionOverwrites,
      enforceRatio,
      objectFit,
      disableResize,
    } = this.props
    const {width} = this.state

    const {desktopSrc, desktopWebpSrc, mobileSrc, mobileWebpSrc, orgSrc, previewSrc} = prepareUrls(
      src || '',
      {
        ...defaultOptions,
        disableResize,
        blur,
        progressive,
        width,
        height:
          objectFit !== 'contain' && enforceRatio ? w => Math.round(w / enforceRatio) : undefined,
        ...optionOverwrites,
      }
    )
    this.setState({
      loaded: false,
      desktopSrc,
      desktopWebpSrc,
      mobileSrc,
      mobileWebpSrc,
      orgSrc,
      previewSrc,
    })
  }

  render() {
    const {
      alt,
      objectFit,
      objectPosition,
      src,
      blur,
      progressive,
      defaultOptions,
      optionOverwrites,
      enforceRatio,
      ...restProps
    } = this.props
    const {
      desktopSrc,
      desktopWebpSrc,
      mobileSrc,
      mobileWebpSrc,
      orgSrc,
      loaded,
      previewSrc,
      containerWidth,
    } = this.state

    let height
    if (containerWidth && enforceRatio) {
      height = Math.round(containerWidth / enforceRatio)
    }

    return (
      <PictureContainer {...restProps}>
        {previewSrc && (
          <StyledPicture className="preview-image" {...restProps} preview loaded={loaded}>
            <StyledImg
              height={height}
              src={previewSrc}
              alt={alt}
              objectFit={objectFit}
              objectPosition={objectPosition}
            />
          </StyledPicture>
        )}
        <StyledPicture {...restProps} ref={this.containerRef}>
          {desktopWebpSrc && (
            <source srcSet={desktopWebpSrc} type="image/webp" media="(min-width: 992px)" />
          )}
          <source srcSet={desktopSrc} type="image/jpg" media="(min-width: 992px)" />
          {mobileWebpSrc && (
            <source srcSet={mobileWebpSrc} type="image/webp" media="(max-width: 992px)" />
          )}
          <source srcSet={mobileSrc} type="image/jpg" media="(max-width: 992px)" />
          <StyledImg
            height={height}
            src={orgSrc}
            alt={alt}
            ref={this.imgRef}
            objectFit={objectFit}
            objectPosition={objectPosition}
            onLoad={() => {
              this.setState({loaded: true})
            }}
          />
        </StyledPicture>
      </PictureContainer>
    )
  }
}

export default props => (
  <ScreenSizeContext.Consumer>
    {({screenSizeUpdate}) => (
      <PictureContext.Consumer>
        {({defaults, overwrites}) => (
          <Picture
            {...props}
            defaultOptions={defaults}
            optionOverwrites={overwrites}
            screenSizeUpdate={screenSizeUpdate}
          />
        )}
      </PictureContext.Consumer>
    )}
  </ScreenSizeContext.Consumer>
)
